import { AsyncPipe } from '@angular/common';
import {
  afterNextRender,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  inject,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  signal,
  SimpleChanges,
  viewChild,
  viewChildren,
} from '@angular/core';
import { FormControl, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerInputEvent,
} from '@angular/material/datepicker';
import { MatIcon } from '@angular/material/icon';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  add,
  addDays,
  addHours,
  addMinutes,
  addMonths,
  addWeeks,
  addYears,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format as formatDate,
  parse,
  startOfDay,
  startOfMonth,
  startOfSecond,
  startOfWeek,
  startOfYear,
  sub,
} from 'date-fns';
import { BehaviorSubject, combineLatest, first, map, Observable } from 'rxjs';

import { isEventTargetType } from '@blaze-runtime/engine-util';
import { ColumnDataType } from '@blaze-shared/api-contracts-domain';
import {
  CanvasElementWithDirtyFieldProps,
  type ElementFormState,
  InputElementMetaUiFeatures,
  makeFontStyles,
  makeFontStyleStyles,
} from '@blaze-shared/elements-domain';
import { getBackground } from '@blaze-shared/elements-domain';
import { DATE_DEFAULT_VALUE, DATETIME_DEFAULT_VALUE } from '@blaze-shared/schema-domain';
import { ColumnType, type ElementSchema, type RuntimeEventType } from '@blaze-shared/schema-domain';
import {
  DatepickerHeaderComponent,
  timeInputBlurFn,
  timeInputPasteFn,
  timeInputTransformFn,
} from '@blaze-shared/shared-feature';
import {
  AutoSlashDateInputDirective,
  BlazeAutocompleteComponent,
  BlazeAutocompleteOptionDirective,
  BlazeAutocompleteOriginDirective,
  BlazeAutocompleteTriggerDirective,
  InputMaskDirective,
  safeDetectChanges,
  ScrollerComponent,
} from '@blaze-shared/shared-ui';
import {
  calculateTimeDiff,
  convertToUTCDate,
  DATE_FORMAT_TO_FNS_MAP,
  DateFormat,
  DateRangeValue,
  dateRangeValueToString,
  DatetimeDefaultDateValue,
  DatetimeDefaultDateValueFunction,
  DatetimeDefaultTimeValue,
  DatetimeDefaultTimeValueFunction,
  dateToUTCWithOffsetAsTime,
  epochToDate,
  epochToDateTime,
  generateDateIgnoringTimezone,
  getDateWithoutTimezone,
  getFirstDayOfWeek,
  hoursMinutesRegex,
  isNullish,
  ISOtoUTCDate,
  isValidDate,
  measureTextSize,
  Meridian,
  regexpMatch,
  splitValueWithUnit,
  TIME_PROPOSAL_ARRAY,
  timeFormatter,
  timeMeridian,
  timeParsingFormats,
  timeToDate,
  useEmitTracker,
  ValueWithUnitOfMeasure,
} from '@blaze-shared/shared-util';
import { FALLBACK_THEME, fontUtils } from '@blaze-shared/theming-domain';

import { CanvasElementComponent } from '../../canvas-elements/element-component';
import { ElementLabelComponent } from '../../components/element-label/element-label.component';
import { CanvasElementBaseComponent } from '../__base/base.component';

import { DateFnsAdapter } from './custom-date-fns-adapter';
import { DynamicFormatDate } from './dynamic-format-date';

@UntilDestroy()
@Component({
  selector: 'be-datetime-input',
  templateUrl: './datetime-input.component.html',
  styleUrls: ['./datetime-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    DynamicFormatDate,
    {
      provide: DateAdapter,
      useClass: DateFnsAdapter,
    },
    {
      provide: MAT_DATE_FORMATS,
      useExisting: DynamicFormatDate,
    },
  ],
  imports: [
    AutoSlashDateInputDirective,
    BlazeAutocompleteComponent,
    AsyncPipe,
    ScrollerComponent,
    BlazeAutocompleteOptionDirective,
    BlazeAutocompleteOriginDirective,
    ReactiveFormsModule,
    BlazeAutocompleteTriggerDirective,
    MatIcon,
    MatDatepicker,
    MatDatepickerInput,
    ElementLabelComponent,
    InputMaskDirective,
  ],
})
export class DatetimeInputComponent
  extends CanvasElementBaseComponent<'datetimeInput'>
  implements CanvasElementComponent<'datetimeInput'>, OnInit, OnChanges, AfterViewInit, OnDestroy
{
  private readonly dynamicFormatDate = inject(DynamicFormatDate);
  private readonly injector = inject(Injector);

  datepicker = viewChild<MatDatepicker<Date>>('datepicker');
  dateInput = viewChild<ElementRef<HTMLInputElement>>('dateInput');
  timeInput = viewChild<ElementRef<HTMLInputElement>>('timeInput');
  timeAutocomplete = viewChild(BlazeAutocompleteComponent<string>);
  timeOptions = viewChildren(BlazeAutocompleteOptionDirective);

  @Input() value?: string | null;
  @Input() formState?: ElementFormState;

  @HostBinding('style.--b-dti-accent-color') accentColor = FALLBACK_THEME.value.accent;

  private outputEmitTracker = useEmitTracker();
  private dataValue = this.getDataInput('value');
  private boundTypeHasDate?: boolean;
  private boundTypeHasTime?: boolean;
  private wasEmitted = false;

  private readonly filterString$ = new BehaviorSubject('');
  private readonly filterByInput$ = new BehaviorSubject(false);

  private defaultDateType: DatetimeDefaultDateValue = 'none';
  private defaultDateFunction?: DatetimeDefaultDateValueFunction;
  private defaultDateOffset?: ValueWithUnitOfMeasure;

  private defaultTimeType: DatetimeDefaultTimeValue = 'none';
  private defaultTimeFunction?: DatetimeDefaultTimeValueFunction;
  private defaultTimeOffset?: ValueWithUnitOfMeasure;

  private defaultTime: Date | null;
  private defaultTimeValue? = '';
  private currentDefaultValue: Date | null = null;

  protected readonly elementKeyRoot = 'b-dti';

  /**
   * Used to keep track of how data should be emitted.
   */
  boundDataType?: ColumnDataType;

  elementDatepickerKey?: string;

  labelEnabled = false;
  labelText?: string;

  styles: Record<string, string> = {};
  inputsContainerStyles: Record<string, string> = {};
  inputStyles: Record<string, string> = {};
  dateWrapperStyles: Record<string, string> = {};
  timeWrapperStyles: Record<string, string> = {};
  validationStyles: Record<string, string> = {};

  dateInputCtrl = new FormControl<Date | null>(null);
  maskedDateInputCtrl = new FormControl('', { updateOn: 'blur' });
  timeInputCtrl = new FormControl('', { updateOn: 'change', nonNullable: true });

  dateFieldEnabled = false;
  timeFieldEnabled = false;
  readonly isReadOnly = signal(false);
  readonly isReadOnlyDate = signal(false);
  readonly isReadOnlyTime = signal(false);

  datepickerHeader = DatepickerHeaderComponent;
  dateObject: Date | null = null;
  minDate?: Date;
  maxDate?: Date;
  minTime?: Date;
  maxTime?: Date;
  lastUsedValue: Date | string | null | undefined = '';
  lastUsedMinDate = '';
  lastUsedMaxDate = '';
  lastUsedMinTime = '';
  lastUsedMaxTime = '';
  currentDateFormat!: DateFormat;
  timeSuffix: Meridian = '';
  useTwelveHoursTimeFormat = false;
  timePlaceholder = '';
  timeProposalArray$: Observable<string[]>;
  isDataValue = false;
  isUserChanged = false;

  get hasValidationError(): string | true | undefined {
    return (
      this.formState?.errors?.required ||
      this.formState?.messages?.endAfterStart ||
      (this.formState?.errors?.invalid as string | undefined)
    );
  }

  get pickedTimeForSelectedMarker(): string {
    return this.timeInputCtrl.value + ' ' + this.timeSuffix;
  }

  get isDatepickerDisabled(): boolean {
    return !!this.props?.content.date?.disableDatepicker;
  }

  get maskedDateInputPlaceholder(): string {
    const userPlaceholder = this.props?.content?.date?.placeholder;
    if (userPlaceholder) {
      return userPlaceholder;
    }

    const format = this.props?.content?.date?.format;

    return format ?? '';
  }

  constructor() {
    super();
    this.timeProposalArray$ = combineLatest({
      input: this.filterString$,
      shouldFilter: this.filterByInput$,
    }).pipe(
      map(({ input, shouldFilter }) => (shouldFilter ? input : '')),
      map((input) => {
        if (!input) {
          return TIME_PROPOSAL_ARRAY;
        }

        return TIME_PROPOSAL_ARRAY.filter((time) =>
          time.toLowerCase().startsWith(input.toLowerCase()),
        );
      }),
    );

    this.maskedDateInputCtrl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      // Emit changes only when masked input is used.
      if (!this.isDatepickerDisabled) {
        return;
      }

      const date = value
        ? parse(value, DATE_FORMAT_TO_FNS_MAP[this.currentDateFormat], new Date())
        : null;

      this.onDateChange(date);
    });

    this.initMarginHandler((props) => props?.style?.general?.offset?.margin);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['props']) {
      if (!this.elementDatepickerKey) {
        this.elementDatepickerKey = `${this.elementKeyRoot}-${this.elementId}`;
      }

      const { title, date, time } = this.props?.content || {};

      this.labelEnabled = title?.enabled ?? true;

      // Update column types of the schema (to Date, Time or DateTime) when date and time toggles change.
      const { currentValue, previousValue } = changes.props;
      if (
        currentValue?.['content']?.['date']?.['enabled'] !==
          previousValue?.['content']?.['date']?.['enabled'] ||
        currentValue?.['content']?.['time']?.['enabled'] !==
          previousValue?.['content']?.['time']?.['enabled']
      ) {
        this.setDefaultEventSchema('change');
      }

      this.isReadOnlyDate.set(date?.readonly ?? false);
      this.isReadOnlyTime.set(time?.readonly ?? false);
    }

    if (changes?.['props'] || changes?.['value']) {
      const defaultUpdated = this.updateDefaultDate();
      if (changes?.['value']) {
        let value = changes?.['value'].currentValue;
        if (!value) {
          value = (this.defaultDateType !== 'none' ? this.currentDefaultValue : value) ?? null;
        }
        this.updateValue(value);
        if (typeof value === 'string') {
          this.updateOutput(value);
        } else {
          this.emitChanges(value);
        }
      }
      if (!defaultUpdated) {
        this.updateDate();
      }
      this.updateTime();
      this.updateStyles();
    }
  }

  ngOnInit(): void {
    this.getElementUiFeatures<InputElementMetaUiFeatures>()
      .pipe(untilDestroyed(this))
      .subscribe((features) => {
        this.labelText = (features.title ?? '') + this.getRequiredSuffix(features);
        this.isReadOnly.set(features.isReadOnly);
        if (this.isReadOnly() && this.dateInputCtrl.enabled) {
          this.dateInputCtrl.disable();
          this.maskedDateInputCtrl.disable();
          this.timeInputCtrl.disable();
        } else if (!this.isReadOnly() && this.dateInputCtrl.disabled) {
          this.dateInputCtrl.enable();
          this.maskedDateInputCtrl.enable();
          this.timeInputCtrl.enable();
        }
        this.updateBoundType();
        this.updateDefaultDate();
        safeDetectChanges(this.cdr);
      });

    if (this.elementDatepickerKey) {
      this.stylesCleanup.register(this.elementDatepickerKey);
    }

    this.setDefaultEventSchema('change');
  }

  ngAfterViewInit(): void {
    if (this.bindingResults?.['value']) {
      if (!this.dateObject && this.currentDefaultValue) {
        this.emitChanges(this.currentDefaultValue);
      }
    } else {
      this.emitChanges(this.dateObject || this.currentDefaultValue || null);
    }

    this.timeInputCtrl.valueChanges
      .pipe(
        map((value) => value ?? ''),
        untilDestroyed(this),
      )
      .subscribe((value) => {
        this.filterByInput$.next(true);
        this.filterString$.next(
          value
            .toLowerCase() // leave just the numeric value for the filter string
            .replace('am', '')
            .replace('pm', '')
            .trim(),
        );
        if (!value || regexpMatch(hoursMinutesRegex, value)) {
          this.onTimeChange(value);
        }
        if (!value) {
          setTimeout(() => this.timeInput()?.nativeElement?.setSelectionRange(0, 0));
        }
      });
  }

  ngOnDestroy(): void {
    if (this.elementDatepickerKey) {
      this.stylesCleanup.dispose(this.elementDatepickerKey);
    }
  }

  override getDefaultEventSchema(eventType: RuntimeEventType): ElementSchema {
    const output = this.ioConfig?.outputs?.find((output) => output.eventType === eventType);
    const defaultSchema = output?.schema;
    const dateEnabled = !!this.props?.content.date?.enabled;
    const timeEnabled = !!this.props?.content.time?.enabled;

    let colDataType: ColumnDataType | ColumnType = 'DateTime';
    if (!dateEnabled) {
      colDataType = 'Time';
    } else if (!timeEnabled) {
      colDataType = 'Date';
    }

    const updatedColumns = defaultSchema?.columns ? [...defaultSchema.columns] : [];
    const column = updatedColumns[0];
    if (column) {
      updatedColumns[0] = {
        ...column,
        columnType: colDataType,
        dataType: colDataType,
      };
    }
    return this.createElementSchema(eventType, updatedColumns, undefined, defaultSchema);
  }

  reset(): void {
    this.outputEmitTracker.reset();
    this.wasEmitted = false;
    this.updateOutput(this.value || '');
  }

  protected onAutocompleteOpen(): void {
    this.filterByInput$.next(false);
  }

  protected timeInputTransform(event: InputEvent, formControl: UntypedFormControl): void {
    timeInputTransformFn(event, formControl, this.useTwelveHoursTimeFormat);
  }

  protected timeInputPaste(event: ClipboardEvent, formControl: UntypedFormControl): void {
    if (this.isReadOnly()) {
      return;
    }
    const changeTimeVariantToPM = timeInputPasteFn(
      event,
      formControl,
      this.useTwelveHoursTimeFormat,
    );
    if (changeTimeVariantToPM) {
      this.timeSuffix = 'PM';
    }
  }

  protected timeInputBlur(event: FocusEvent, formControl: UntypedFormControl): void {
    // omit blur effect when clicked on proposed time from menu
    if ((event.relatedTarget as HTMLElement)?.closest('.inline-editor-time-picker')) {
      return;
    } else {
      this.filterString$.next('');
    }
    timeInputBlurFn(formControl, this.useTwelveHoursTimeFormat);
  }

  protected onMaskedDateInput(event: any): void {
    const value = event.target.value
      ? parse(event.target.value, DATE_FORMAT_TO_FNS_MAP[this.currentDateFormat], new Date())
      : null;
    // Emit changes from datepicker input only when datepicker is enabled
    if ((value && !isValidDate(value)) || !this.isDatepickerDisabled) {
      return;
    }

    this.isUserChanged = true;
    this.onDateChange(value);
  }

  protected onInput(event: Event): void {
    if (!isEventTargetType<HTMLInputElement>(event)) {
      return;
    }
    const targetValue = event.target.value ?? '';
    const value = targetValue
      ? parse(targetValue, DATE_FORMAT_TO_FNS_MAP[this.currentDateFormat], new Date())
      : null;
    // Emit changes from datepicker input only when datepicker is enabled
    if ((value && !isValidDate(value)) || this.isDatepickerDisabled) {
      return;
    }

    this.isUserChanged = true;
    this.onDateChange(value, true);
  }

  protected onDatepickerInputChange(event: MatDatepickerInputEvent<Date>): void {
    // Emit changes from datepicker only when it is enabled
    if (this.isDatepickerDisabled) {
      return;
    }
    if (event.value) {
      const neutralizedDate = dateToUTCWithOffsetAsTime(event.value);
      if (this.dateObject) {
        neutralizedDate.setHours(this.dateObject.getHours(), this.dateObject.getMinutes());
      } else {
        neutralizedDate.setHours(0, 0);
      }
      this.onDateChange(neutralizedDate);
    } else {
      this.onDateChange(null);
    }
    this.isUserChanged = true;
  }

  protected openDatepicker(): void {
    if (this.isReadOnly()) {
      return;
    }

    this.datepicker()?.open();

    setTimeout(() => {
      const datepicker = this.datepicker();
      if (!datepicker || !datepicker.opened) {
        return;
      }

      const datepickerHtml = document.getElementById(datepicker.id);
      const datepickerContentHtml = datepickerHtml?.closest('mat-datepicker-content');

      if (datepickerContentHtml && this.elementDatepickerKey) {
        datepickerContentHtml.classList.add(this.elementKeyRoot, this.elementDatepickerKey);
      }
    });
  }

  protected clearDateInput(): void {
    this.dateInputCtrl.setValue(null);
    this.maskedDateInputCtrl.setValue(null);
    this.onDateChange(null);
  }

  protected changeTimeSuffix(): void {
    if (this.isReadOnly() || this.isReadOnlyTime()) {
      return;
    }

    const suffix = this.getTimeSuffix();
    if (!suffix || !this.dateObject) {
      this.timeSuffix = this.timeSuffix === 'PM' ? 'AM' : 'PM';
      return;
    }
    this.timeSuffix = suffix === 'PM' ? 'AM' : 'PM';

    if (suffix === 'AM') {
      this.emitChanges(add(this.dateObject, { hours: 12 }));
    } else {
      this.emitChanges(sub(this.dateObject, { hours: 12 }));
    }
  }

  protected openTimePicker(): void {
    const timeInput = this.timeInput()?.nativeElement;
    if (this.isReadOnly() || !timeInput) {
      return;
    }

    const omitSelection = timeInput.selectionStart !== timeInput.selectionEnd;

    this.timeAutocomplete()
      ?.opened.asObservable()
      .pipe(first())
      .subscribe(() => {
        afterNextRender(
          () => {
            const selectedOption = this.timeOptions().find((option) => option.selected());

            if (selectedOption) {
              selectedOption.getHostElement().scrollIntoView();
            }

            if (!omitSelection) {
              this.timeInput()?.nativeElement.select();
            }
          },
          { injector: this.injector },
        );
      });
  }

  private emitChanges(value?: Date | null, preventDateInputValueSet = false): void {
    this.dateObject = value || null;

    if (this.dateObject) {
      this.updateDate(preventDateInputValueSet);
      this.updateTime();
    }

    const epoch = this.dateObject ? this.dateObject.getTime() : null;

    if (this.boundDataType === 'Date' || !this.timeFieldEnabled) {
      this.updateOutput(epochToDate(epoch));
    } else {
      this.updateOutput(epochToDateTime(epoch));
    }
  }

  private onDateChange(value: Date | null, preventDateInputValueSet = false): void {
    if (!value) {
      this.emitChanges(undefined);
      return;
    }

    const valueWithoutTime = new Date(value).setHours(0, 0, 0, 0);
    const minDiff = this.minDate ? this.minDate.getTime() - valueWithoutTime : -1;
    const maxDiff = this.maxDate ? this.maxDate.getTime() - valueWithoutTime : 1;

    if (minDiff > 0 || maxDiff < 0 || !value || !isValidDate(value)) {
      this.dateInputCtrl.setValue(this.dateObject);
      this.maskedDateInputCtrl.setValue(null, { emitEvent: false });
    } else {
      /**
       * Material datepicker does not include information about time.
       * It has to be added manually to change event
       */
      const newDate = new Date(value);
      newDate.setHours(this.dateObject?.getHours() || 0);
      newDate.setMinutes(this.dateObject?.getMinutes() || 0);

      this.emitChanges(value, preventDateInputValueSet);
    }
  }

  private onTimeChange(value: string): void {
    if (!value) {
      return this.emitChanges();
    }

    let parsed;

    if (this.useTwelveHoursTimeFormat) {
      const isSuffixIncluded = regexpMatch(timeMeridian, value);
      const newValueToParse = (value || '00:00') + (isSuffixIncluded ? '' : ` ${this.timeSuffix}`);
      parsed = parse(
        newValueToParse,
        timeParsingFormats.timeInputTwelveHoursWithSuffix,
        new Date(),
      );
    } else {
      parsed = parse(value || '00:00', timeParsingFormats.timeInput, new Date());
    }

    let hoursToSet = parsed.getHours();
    let minutesToSet = parsed.getMinutes();

    if (this.minTime) {
      const diff = calculateTimeDiff(parsed, this.minTime);
      if (diff > 0) {
        hoursToSet = this.minTime.getHours();
        minutesToSet = this.minTime.getMinutes();
      }
    }
    if (this.maxTime) {
      const diff = calculateTimeDiff(parsed, this.maxTime);
      if (diff < 0) {
        hoursToSet = this.maxTime.getHours();
        minutesToSet = this.maxTime.getMinutes();
      }
    }

    parsed.setHours(hoursToSet);
    parsed.setMinutes(minutesToSet);

    /**
     * Update model
     */
    if (this.dateObject) {
      this.dateObject.setHours(hoursToSet);
      this.dateObject.setMinutes(minutesToSet);
    } else if (this.dateInputCtrl.value && isValidDate(this.dateInputCtrl.value)) {
      this.dateObject = new Date(this.dateInputCtrl.value);
      this.dateObject.setHours(hoursToSet);
      this.dateObject.setMinutes(minutesToSet);
    }

    this.emitChanges(this.dateObject || parsed);
  }

  private closeDatePicker(): void {
    this.datepicker()?.close();
  }

  private getTimeSuffix(): Meridian {
    return this.dateObject ? (formatDate(this.dateObject, 'aa') as Meridian) : '';
  }

  private updateStyles(): void {
    const { style, content } = this.props || {};
    const { title } = content || {};
    const { general, input, inputFont, labelBlock } = style || {};
    if (!style || !title || !general || !input || !inputFont || !labelBlock) {
      return;
    }

    const styles: Record<string, string> = {};
    const inputsContainerStyles: Record<string, string> = {};
    const inputWrapperStyles: Record<string, string> = {
      background: getBackground(input.fill),
      color: input.value.color1,
    };
    const inputStyles: Record<string, string> = {
      ...makeFontStyles(style, { fontSize: true, lineHeight: true }),
      ...makeFontStyles(inputFont),
      ...makeFontStyleStyles(inputFont.style),
      ['font-weight']: this.fontsService.getFontWeight(inputFont.fontFamily, inputFont.fontWeight),
    };
    const validationStyles: Record<string, string> = {};

    if (title.enabled !== false) {
      if (title.position === 'top') {
        styles['flex-direction'] = `column`;
        inputsContainerStyles['width'] = '100%';
      } else {
        validationStyles['margin-left'] = title.width;
        validationStyles['padding-left'] = '8px';
        inputsContainerStyles['flex-basis'] = `${100 - parseInt(title.width, 10)}%`;
      }
    }

    if (input.border.selectorType !== 'none') {
      inputWrapperStyles['border'] = `1px solid ${input.border.color1}`;
    }
    if (input.radius !== '0px') {
      inputWrapperStyles['border-radius'] = input.radius;
    }

    this.styles = styles;
    this.inputsContainerStyles = inputsContainerStyles;
    this.dateWrapperStyles = inputWrapperStyles;
    this.inputStyles = inputStyles;
    this.validationStyles = validationStyles;
    this.accentColor = general.accent.color1;
    this.timeWrapperStyles = { ...inputWrapperStyles, ...inputStyles };
    const font = fontUtils.createFontValueCSS(
      inputFont.fontFamily,
      inputFont.style.includes('bold'),
      inputFont.style.includes('italic'),
      inputFont.fontSize,
    );
    fontUtils
      .fromFontFaceObserver(font, '00:00 AM')
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        const res = measureTextSize('00:00 AM', font);
        // Update time input styles based on the measured width + additional paddings/margins inside this element
        // .time-suffix: mr-4,ml-8
        // .input-time: ml-4, p-8
        const additionalPaddingMargin = `0.308rem + 0.616rem + 0.308rem + 16px`;
        this.timeWrapperStyles['max-width'] = `calc(${res.width}px + ${additionalPaddingMargin})`;
        safeDetectChanges(this.cdr);
      });
  }

  private updateDefaultTime(): boolean {
    const { time } = this.props?.content ?? {};

    const defaultTimeType = time?.defaultValue ?? 'none';
    const defaultTimeFunction = time?.defaultValueFunction;
    const defaultTimeOffset = time?.defaultValueOffset;
    const defaultTimeValue = time?.defaultTime;
    const timeFormat = time?.format;

    let hasChanges = false;
    if (
      this.defaultTimeType !== defaultTimeType ||
      this.defaultTimeFunction !== defaultTimeFunction ||
      this.defaultTimeOffset !== defaultTimeOffset ||
      this.defaultTimeValue !== defaultTimeValue
    ) {
      this.defaultTime = offsetDateTimeValue(
        getTimeValue(defaultTimeType, defaultTimeFunction, defaultTimeValue),
        defaultTimeOffset,
      );
      this.defaultTimeType = defaultTimeType;
      this.defaultTimeFunction = defaultTimeFunction;
      this.defaultTimeOffset = defaultTimeOffset;
      this.defaultTimeValue = defaultTimeValue;
      hasChanges = true;
    }

    if (this.defaultTime) {
      this.timePlaceholder = formatDate(
        this.defaultTime,
        timeFormat === '12h'
          ? timeParsingFormats.timeInputTwelveHours
          : timeParsingFormats.timeInput,
      );
      if (!this.timeInputCtrl.value) {
        this.timeSuffix =
          timeFormat === '12h' ? (formatDate(this.defaultTime, 'aa') as Meridian) : '';
      }
    } else {
      if (timeFormat === '12h') {
        this.timePlaceholder = '12:00';
      } else {
        this.timePlaceholder = '24:00';
      }
      if (!this.timeInputCtrl.value) {
        this.timeSuffix = this.getTimeSuffix() || 'AM';
      }
    }

    if (this.boundDataType === 'Time' && this.defaultTime && !this.dateObject) {
      this.dateObject = this.defaultTime;
      this.updateTime();
      this.emitChanges(this.dateObject);
    }

    return hasChanges;
  }

  /** @returns whether updateDate() was called */
  private updateDefaultDate(): boolean {
    const hasTimeChanges = this.updateDefaultTime();

    const { date } = this.props?.content ?? {};
    const defaultDateType = date?.defaultValue || 'none';
    const defaultDateFunction = date?.defaultValueFunction;
    const defaultDateOffset = date?.defaultValueOffset;

    if (
      !hasTimeChanges &&
      this.defaultDateType === defaultDateType &&
      this.defaultDateFunction === defaultDateFunction &&
      this.defaultDateOffset === defaultDateOffset
    ) {
      return false;
    }

    this.defaultDateType = defaultDateType;
    this.defaultDateFunction = defaultDateFunction;
    this.defaultDateOffset = defaultDateOffset;

    let updateTime = hasTimeChanges;
    if (this.defaultDateType === 'today') {
      if (this.boundDataType === 'Date') {
        this.currentDefaultValue = new Date(new Date().setHours(0, 0, 0, 0));
      } else if (this.defaultTime) {
        this.currentDefaultValue = new Date();
        updateTime = true;
      } else {
        this.currentDefaultValue = new Date();
      }
    } else if (this.defaultDateType === 'other') {
      this.currentDefaultValue = offsetDateTimeValue(
        getDateValue(defaultDateFunction),
        defaultDateOffset,
      );
      updateTime ||= this.boundDataType === 'Date';
    } else if (this.defaultTime) {
      this.currentDefaultValue = this.defaultTime;
    } else {
      this.currentDefaultValue = null;
    }

    if (this.currentDefaultValue && this.defaultTime && updateTime) {
      this.currentDefaultValue.setHours(
        this.defaultTime.getHours(),
        this.defaultTime.getMinutes(),
        this.defaultTime.getSeconds(),
        this.defaultTime.getMilliseconds(),
      );
    }

    if (!this.isUserChanged && !this.isDataValue) {
      this.dateObject = this.defaultDateType === 'none' ? null : this.currentDefaultValue;
      this.updateDate();
      this.emitChanges(this.currentDefaultValue ?? undefined);
      return true;
    }
    return false;
  }

  /**
   * Update date field settings and refresh datepicker.
   * @param preventDateInputValueSet - when true, date input value change will not emit valueChanges event, it is needed only for onInput function call from <input />
   */
  private updateDate(preventDateInputValueSet = false): void {
    const { content } = this.props || {};
    const { dirty } = (this.props as CanvasElementWithDirtyFieldProps) || {};
    const { date } = content || {};

    if (!date) {
      return;
    }

    const { enabled, format, min, max } = date;

    this.dateFieldEnabled =
      this.boundTypeHasDate !== undefined && !dirty?.displayVariant
        ? this.boundTypeHasDate
        : !!enabled;

    this.setLastUsedMinMax(min, max);
    this.setDynamicFormat(format);

    /**
     * Refresh datepicker.
     */
    if (this.datepicker()?.opened) {
      this.closeDatePicker();
    }

    const dateToUse = this.dateObject ?? this.currentDefaultValue;
    const inputValue =
      (dateToUse &&
        isValidDate(dateToUse) &&
        formatDate(dateToUse, DATE_FORMAT_TO_FNS_MAP[this.currentDateFormat])) ||
      '';

    this.maskedDateInputCtrl.setValue(inputValue, { emitEvent: false });
    if (!preventDateInputValueSet) {
      if (!this.dateObject) {
        this.dateInputCtrl.setValue(null);
        return;
      }
      const dateInUtc: Date = convertToUTCDate(this.dateObject);
      const value = (dateInUtc && ISOtoUTCDate(dateInUtc.toISOString())) || null;
      this.dateInputCtrl.setValue(value);
    }
  }

  private setLastUsedMinMax(min: string | DateRangeValue, max: string | DateRangeValue): void {
    const maxDate = dateRangeValueToString(max);
    const minDate = dateRangeValueToString(min);
    if (minDate !== this.lastUsedMinDate) {
      this.minDate = minDate ? generateDateIgnoringTimezone(minDate) : undefined;
      this.lastUsedMinDate = minDate;
    }
    if (maxDate !== this.lastUsedMaxDate) {
      this.maxDate = maxDate ? generateDateIgnoringTimezone(maxDate) : undefined;
      this.lastUsedMaxDate = maxDate;
    }
  }

  private setDynamicFormat(format: DateFormat): void {
    if (format !== this.currentDateFormat) {
      this.currentDateFormat = format;
      this.dynamicFormatDate.setDisplayFormat({ dateInput: DATE_FORMAT_TO_FNS_MAP[format] });
      this.dynamicFormatDate.setParseFormat({ dateInput: DATE_FORMAT_TO_FNS_MAP[format] });
    }
  }

  private updateTime(): void {
    const { content } = this.props || {};
    const { dirty } = (this.props as CanvasElementWithDirtyFieldProps) || {};
    const { time } = content || {};

    if (!time) {
      return;
    }

    const { enabled, format, min, max } = time;
    const useTwelveHoursTimeFormat = format === '12h';

    this.timeFieldEnabled =
      this.boundTypeHasTime !== undefined && !dirty?.displayVariant
        ? this.boundTypeHasTime
        : !!enabled;

    let sTime = '';

    if (!this.timeInputCtrl.value && this.defaultTime) {
      const time = this.defaultTime;
      this.dateObject?.setHours(time.getHours(), time.getMinutes());
    }

    if (useTwelveHoursTimeFormat) {
      sTime =
        (this.dateObject && formatDate(this.dateObject, timeParsingFormats.timeInputTwelveHours)) ||
        '';
      this.timeSuffix = this.getTimeSuffix() || 'AM';
    } else {
      sTime = (this.dateObject && formatDate(this.dateObject, timeParsingFormats.timeInput)) || '';
      this.timeSuffix = '';
    }

    if (min !== this.lastUsedMinTime) {
      this.minTime = parse(min, timeParsingFormats.timeProp, new Date());
      this.lastUsedMinTime = min;
    }

    if (max !== this.lastUsedMaxTime) {
      this.maxTime = parse(max, timeParsingFormats.timeProp, new Date());
      this.lastUsedMaxTime = max;
    }

    this.useTwelveHoursTimeFormat = useTwelveHoursTimeFormat;
    this.timeInputCtrl.setValue(sTime, { emitEvent: false });
  }

  private updateBoundType(): void {
    this.boundDataType = this.dataValue.column?.dataType || 'DateTime';
    const [boundTypeHasDate, boundTypeHasTime] = this.dataValue.column
      ? [
          this.boundDataType === 'DateTime' || this.boundDataType === 'Date',
          this.boundDataType === 'DateTime' || this.boundDataType === 'Time',
        ]
      : [];

    if (boundTypeHasDate !== this.boundTypeHasDate) {
      this.boundTypeHasDate = boundTypeHasDate;
      this.updateDate();
    }
    if (boundTypeHasTime !== this.boundTypeHasTime) {
      this.boundTypeHasTime = boundTypeHasTime;
      this.updateTime();
    }
  }

  private isTimeInput(): boolean {
    return (
      (this.boundDataType === 'Time' || !this.props?.content.date?.enabled) &&
      !!this.props?.content.time?.enabled
    );
  }

  private updateValue(value: Date | string | null | undefined): void {
    if (value === DATE_DEFAULT_VALUE) {
      value = getDateWithoutTimezone(new Date());
    } else if (value === DATETIME_DEFAULT_VALUE) {
      value = startOfSecond(new Date()).toISOString();
    }

    if (value === this.lastUsedValue) {
      return;
    }

    if (this.isTimeInput() && value) {
      this.dateObject = value instanceof Date ? value : timeToDate(value);
    } else if (value instanceof Date) {
      this.dateObject = value;
    } else if (value) {
      if (value.includes('T')) {
        this.dateObject = new Date(value);
      } else {
        this.dateObject = ISOtoUTCDate(value);
      }
    } else {
      this.dateObject = null;
    }

    this.isDataValue = !isNullish(this.dateObject);

    this.lastUsedValue = value;
  }

  private updateOutput(data: string | null = ''): void {
    if (data === null) {
      data = '';
    }
    this.value = data;
    if (this.outputEmitTracker.shouldEmit(data)) {
      this.output.emit({
        eventType: 'change',
        data: this.isTimeInput() ? timeFormatter(data, false, true) : data,
        isInitialData: !this.wasEmitted,
        schema: this.getDefaultEventSchema('change'),
      });
      this.wasEmitted = true;
    }
  }
}

function getTimeValue(
  defaultTimeType: DatetimeDefaultTimeValue,
  timeFunction: DatetimeDefaultTimeValueFunction | undefined,
  defaultValue: string | undefined,
): Date | undefined {
  if (!timeFunction || defaultTimeType === 'none') {
    return undefined;
  }

  const today = new Date();

  if (defaultTimeType === 'now') {
    return today;
  }

  // defaultTimeType === 'custom'
  switch (timeFunction) {
    case 'startOfDay':
      return startOfDay(today);
    case 'noon':
      return new Date(today.setHours(12, 0, 0, 0));
    case 'endOfDay':
      return endOfDay(today);
    case 'custom':
      return defaultValue ? parse(defaultValue, 'HH:mm', new Date()) : undefined;
  }
}

function getDateValue(
  dateFunction: DatetimeDefaultDateValueFunction | undefined,
): Date | undefined {
  if (!dateFunction) {
    return undefined;
  }
  const today = new Date();
  const weekStartsOn = getFirstDayOfWeek() === 'sun' ? 0 : 1;
  switch (dateFunction) {
    case 'startOfWeek':
      return startOfWeek(today, { weekStartsOn });
    case 'endOfWeek':
      return endOfWeek(today, { weekStartsOn });
    case 'startOfMonth':
      return startOfMonth(today);
    case 'endOfMonth':
      return endOfMonth(today);
    case 'startOfNextMonth':
      return addMonths(startOfMonth(today), 1);
    case 'startOfYear':
      return startOfYear(today);
  }
}

function offsetDateTimeValue(
  source: Date | undefined,
  offset: ValueWithUnitOfMeasure | undefined,
): Date | null {
  if (!source) {
    return null;
  }
  const { value, unit } = splitValueWithUnit(offset) ?? {};
  if (isNullish(value) || !unit) {
    return source;
  }

  switch (unit) {
    case 'min':
      return addMinutes(source, value);
    case 'hrs':
      return addHours(source, value);
    case 'days':
      return addDays(source, value);
    case 'weeks':
      return addWeeks(source, value);
    case 'months':
      return addMonths(source, value);
    case 'years':
      return addYears(source, value);
  }

  return source;
}
